'use client';

import React, { memo, useCallback, useMemo, useState } from 'react';

import Table from '@wconcept/wconcept-ui-renewal/components/atoms/Table';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import ContTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ContTitle';
import {
  IconArrowDown02,
  IconArrowRight11,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import {
  classNameBind,
  setComma,
  setNameMasking,
} from '@wconcept/wconcept-common/utils/commonUtils';

import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

/**
 * 회원명을 공백 단위로 끊어 setNameMasking을 적용한다.
 *   김남준 → 김*준 / David Beckham → D***d B*****m
 */
const maskName = (name?: string) =>
  (name ?? '')
    .split(' ')
    .map(token => setNameMasking(token))
    .join(' ');

/** Table 아톰에 넘기는 행. PC는 좌우 2벌(rank/rank2 …)이 한 행에 담긴다. */
type RankRow = Record<string, string | number | undefined>;

export interface IPurchaseKingRank {
  /** 순위 */
  rank: number;
  /** 마스킹된 회원명 */
  memberName: string;
  /** 누적 구매 금액 */
  amount: number;
}

interface IProps {
  /** 랭킹 타이틀 */
  title?: string;
  /** 집계 기준 안내 문구 */
  subTitle?: string;
  /** 내 누적 구매 금액 박스 라벨 */
  myAmountLabel?: string;
  /** 내 누적 구매 금액 (로그인 시에만 노출) */
  myAmount?: number;
  /** 랭킹 리스트 */
  rankList?: IPurchaseKingRank[];
  /** 로그인 여부 */
  isLogin?: boolean;
  /** 비로그인 시 금액 자리에 노출할 문구 */
  notLoginText?: string;
  /** 비로그인 시 노출할 버튼 문구 */
  loginText?: string;
  /** 접힌 상태에서 노출할 랭킹 개수 */
  foldCount?: number;
  /** 비로그인 시 dim 뒤로 노출할 인원 수 (모바일) */
  notLoginMobileCount?: number;
  /** 비로그인 시 dim 뒤로 노출할 행 수 (PC, 한 행에 좌우 2명 → 6이면 좌측 1~6위) */
  notLoginDesktopRowCount?: number;
  /** PC 여부. PC는 20위를 좌/우 2단으로 한 번에 노출한다 */
  isDesktop?: boolean;
  /** 더보기 버튼 문구 */
  moreText?: string;
  /** 컬러 타입 */
  colorType?: ColorType;
  /** 데이터가 없을 때 노출할 문구 */
  noDataMessage?: string;
  /** 로그인 버튼 클릭 */
  onLoginClick?: () => void;
  /** 더보기 클릭 (펼침과 별개로 로깅 등에 사용) */
  onMoreClick?: () => void;
}

const PurchaseKingSection = ({
  title = '상위 TOP 20',
  subTitle,
  myAmountLabel = '나의 누적 구매 금액',
  myAmount,
  rankList = [],
  isLogin = false,
  notLoginText = '로그인 후에 확인 가능해요',
  loginText = '로그인 하기',
  foldCount = 10,
  notLoginMobileCount = 5,
  notLoginDesktopRowCount = 3,
  isDesktop = false,
  moreText = '랭킹 더보기',
  colorType = 'white',
  noDataMessage = '실적을 집계하고 있어요. 순위가 완성되면 보여드릴게요.',
  onLoginClick,
  onMoreClick,
}: IProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const cName = classNameBind([
    'purchase-king',
    !isCustomColor ? `bg-type-${colorType}` : '',
    isLogin ? '' : 'is-not-login',
  ]);

  // 목록을 반으로 갈라 한 행에 좌우 2명씩 담는다.
  const toPcRows = useCallback((list: IPurchaseKingRank[]): RankRow[] => {
    const half = Math.ceil(list.length / 2);
    return Array.from({ length: half }, (_, i) => ({
      rank: list[i]?.rank,
      memberName: list[i]?.memberName,
      amount: list[i]?.amount,
      rank2: list[i + half]?.rank,
      memberName2: list[i + half]?.memberName,
      amount2: list[i + half]?.amount,
    }));
  }, []);

  // 로그인 여부와 접힘 상태에 따라 노출할 목록을 자른다.
  const viewList = useMemo(() => {
    if (isDesktop)
      return toPcRows(
        isLogin ? rankList : rankList.slice(0, notLoginDesktopRowCount * 2)
      );
    if (!isLogin) return rankList.slice(0, notLoginMobileCount);
    return isExpanded ? rankList : rankList.slice(0, foldCount);
  }, [
    isDesktop,
    isLogin,
    isExpanded,
    toPcRows,
    notLoginDesktopRowCount,
    rankList,
    notLoginMobileCount,
    foldCount,
  ]);

  const hasMore =
    !isDesktop && isLogin && !isExpanded && rankList.length > foldCount;

  const handleMoreClick = useCallback(() => {
    setIsExpanded(true);
    onMoreClick?.();
  }, [onMoreClick]);

  // 1위에 포인트 컬러 클래스를 붙인다.
  const mark = useCallback(
    (value: React.ReactNode, rank?: number | string) => (
      <span className={Number(rank) === 1 ? 'is-top' : ''}>{value}</span>
    ),
    []
  );

  // 순위/회원명/금액 3칸을 한 벌로 만든다. PC는 좌·우로 두 벌을 쓴다.
  const buildColumns = useCallback(
    (suffix: '' | '2') => [
      {
        key: `rank${suffix}`,
        header: '순위',
        align: 'center' as const,
        headerAlign: 'center' as const,
        render: (value: number) => mark(value, value),
      },
      {
        key: `memberName${suffix}`,
        header: '회원명',
        align: 'center' as const,
        headerAlign: 'center' as const,
        render: (value: string, row: RankRow) =>
          mark(maskName(value), row[`rank${suffix}`]),
      },
      {
        key: `amount${suffix}`,
        header: '누적 구매 금액(원)',
        align: 'right' as const,
        headerAlign: 'right' as const,
        render: (value: number, row: RankRow) =>
          // null/undefined를 String()에 그대로 넘기면 'null'이 찍힌다.
          mark(
            value == null ? '' : setComma(String(value)),
            row[`rank${suffix}`]
          ),
      },
    ],
    [mark]
  );

  const columns = useMemo(
    () =>
      isDesktop
        ? [...buildColumns(''), ...buildColumns('2')]
        : buildColumns(''),
    [isDesktop, buildColumns]
  );

  return (
    <S.PurchaseKingSection
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
      $pointColor={customColors?.pointColor}
      $emphasisTextColor={customColors?.emphasisTextColor}
    >
      <div className="my-amount-box">
        <Text className="my-amount-label" block>
          {myAmountLabel}
        </Text>
        <Text className="my-amount-value" block>
          {isLogin ? (
            <>
              {setComma(String(myAmount ?? 0))}
              <span className="unit">원</span>
            </>
          ) : (
            notLoginText
          )}
        </Text>
      </div>

      <div className="ranking-wrap">
        <div className="ranking-inner" aria-hidden={!isLogin}>
          <ContTitle
            title={title}
            subTitle={subTitle}
            titleColor={customColors?.textColor}
            subTitleColor={customColors?.textColor}
          />
          <div className="ranking-table">
            <Table
              columns={columns}
              data={viewList}
              noDataMessage={noDataMessage}
            />
          </div>
          {hasMore && (
            <Button className="ranking-more-btn" onClick={handleMoreClick}>
              {moreText}
              <IconArrowDown02 />
            </Button>
          )}
        </div>

        {!isLogin && (
          <div className="login-dim">
            <Button className="login-btn" onClick={onLoginClick}>
              {loginText}
              <IconArrowRight11 />
            </Button>
          </div>
        )}
      </div>
    </S.PurchaseKingSection>
  );
};

export default memo(PurchaseKingSection);
